import { useId, type ReactNode } from "react";
import { ArrowLeft, FileDown } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { formatPhp } from "../lib/money";
import { ContractHeaderActions } from "./ContractHeaderActions";
import { Tabs, TabsList, TabsTrigger } from "./beui/Tabs";
import { WorkspaceMetadata } from "./WorkspaceMetadata";
import "./MobileContractWorkspace.css";

export type MobileContractTab = "overview" | "milestones" | "work" | "payment" | "agreement" | "activity";

/** Presentation only. The Contracts page retains source/role rules and all mutations. */
type MobileContractHeader = {
  title: string;
  sourceLabel: string;
  counterparty: string;
  counterpartyLabel: string;
  statusLabel: string;
  statusClass: string;
  messageHref: string | null;
  jobContract?: boolean;
} & ({ kind: "job" } | { kind?: "contract"; amount: number; releasedAmount: number; escrowAmount: number;
  isOrder: boolean; hasReceipt: boolean });

type MobileContractWorkspaceProps = {
  header: MobileContractHeader;
  activeTab: MobileContractTab;
  error: string | null;
  onBack: () => void;
  onTabChange: (tab: MobileContractTab) => void;
  onDownload?: (kind: "contract" | "receipt") => void;
  children: ReactNode;
  actions?: ReactNode;
  headerActions?: ReactNode;
  activityAvailable?: boolean;
};

export function MobileContractWorkspace({ header, activeTab, error, onBack, onTabChange, onDownload, children, actions, headerActions, activityAvailable = true }: MobileContractWorkspaceProps) {
  const { t } = useI18n();
  const tabsId = useId();
  const tabs: readonly MobileContractTab[] = header.kind === "job" || header.jobContract ? ["overview", "milestones", "work", "agreement", "activity"] : header.isOrder ? ["overview", "payment"] : activityAvailable ? ["overview", "work", "payment", "activity"] : ["overview", "work", "payment"];
  const documentActions = header.kind !== "job" && <>
    {!header.jobContract && <button type="button" onClick={() => onDownload?.("contract")}><FileDown className="h-4 w-4" aria-hidden="true" />{t("contracts.detail.contractPdf")}</button>}
    {header.hasReceipt && <button type="button" onClick={() => onDownload?.("receipt")}><FileDown className="h-4 w-4" aria-hidden="true" />{t("contracts.detail.receiptPdf")}</button>}
    {actions}
  </>;
  return (
    <div className="mobile-contract-workspace">
      <button type="button" className="mobile-contract-back" onClick={onBack}><ArrowLeft aria-hidden="true" />{t("contracts.detail.back")}</button>
      {error && <p className="mobile-contract-error" role="alert">{error}</p>}
      <MobileContractHeading header={header} headerActions={headerActions} actions={documentActions} />
      <nav className="mobile-contract-tabs" aria-label={t("contracts.detail.sections")}>
        <Tabs value={activeTab} variant="underline" onValueChange={value => { const next = tabs.find(tab => tab === value); if (next) onTabChange(next); }}>
          <TabsList aria-label={t("contracts.detail.sections")}>
            {tabs.map(tab => <TabsTrigger key={tab} value={tab} id={`${tabsId}-${tab}`} aria-controls={`${tabsId}-panel`} indicatorClassName="h-[3px] bg-brand-red">
              {tab === "activity" ? "Activity" : tab === "milestones" ? "Milestones" : t(`contracts.detail.${tab}`)}
            </TabsTrigger>)}
          </TabsList>
        </Tabs>
      </nav>
      <div id={`${tabsId}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-${activeTab}`} tabIndex={0}>{children}</div>
    </div>
  );
}

function MobileContractHeading({ header, headerActions, actions }: { header: MobileContractHeader; headerActions?: ReactNode; actions?: ReactNode }) {
  const { t } = useI18n();
  const initials = header.counterparty.trim().split(/\s+/).slice(0, 2).map((part) => part.charAt(0)).join("").toUpperCase() || "?";
  return (
    <header className="mobile-contract-heading">
      <div className="mobile-contract-heading-top"><div className="mobile-contract-source"><span>{header.sourceLabel}</span><span className={`mobile-contract-status ${header.statusClass}`}>{header.statusLabel}</span></div>
        <ContractHeaderActions messageHref={header.messageHref} messageLabel={t("contracts.detail.message")} primaryActions={headerActions} actions={actions} /></div>
      <h1 title={header.title}>{header.title}</h1>
      <div className="mobile-contract-party"><span className="mobile-contract-party-avatar" aria-hidden="true">{initials}</span>
        <WorkspaceMetadata label={`${header.counterpartyLabel}: ${header.counterparty || "—"}`} amount={header.kind !== "job" ? header.amount : undefined} /></div>
      {header.kind !== "job" && !header.isOrder && <div className="mobile-contract-funds"><div><small>{t("contracts.detail.released")}</small><strong>{formatPhp(header.releasedAmount)}</strong></div>
        <div><small>{t("contracts.detail.escrow")}</small><strong>{formatPhp(header.escrowAmount)}</strong></div></div>}
    </header>
  );
}
